import { router, Stack } from "expo-router";
import { useState } from "react";
import { View } from "react-native";
import { Button } from "~/components/Button";
import { NumberInput } from "~/components/form/NumberInput";
import { RadioSelector } from "~/components/form/radio/RadioSelector";
import { OnboardingSkip } from "~/components/layout/home/OnboardingSkip";
import { Page } from "~/components/layout/Page";
import { Colour, Title } from "~/components/layout/text";
import { Authorize } from "~/components/permission/Authorize";
import { Nothing } from "~/components/permission/Nothing";
import { Flex, Gap, Padding } from "~/constants/layout";
import { useFeedback } from "~/providers/FeedbackProvider";
import { useProfile } from "~/providers/ProfileProvider";
import type { NumericString } from "~/util/string";

export default function PeriodPredictability() {
    const { cycle, updateCycleSettings } = useProfile();
    const [predictable, setPredictable] = useState<string>("");
    const [length, setLength] = useState(cycle.period);
    const { saving, setSaving } = useFeedback();

    const next = cycle.periods.length ? "/on-boarding/period-prediction" : "/on-boarding/last-period";

    const savePeriod = async () => {
        if (saving) {
            return;
        }

        if (predictable === "Yes") {
            setSaving(true);

            await updateCycleSettings({
                ...cycle,
                period: length,
            }).finally(() => setSaving(false));
        }

        router.replace(next);
    };

    return (
        <>
            <Stack.Screen
                options={{
                    headerLeft: () => <Nothing />,
                    headerRight: () => <OnboardingSkip next={next} />,
                }}
            />
            <Authorize ability="update" section="cycle">
                <Page gradient spacer>
                    <View style={[Flex.between, Flex.grow, Padding.bottom_xxl]}>
                        <View style={[Gap.xxl, Padding.horizontal_xs]}>
                            <RadioSelector
                                title={
                                    <Title size="xl">
                                        Is the length of your <Colour colour="brand">period</Colour> predictable?
                                    </Title>
                                }
                                options={["Yes", "No", "I don’t know"]}
                                value={predictable}
                                onValueChange={setPredictable}
                            />
                            {predictable === "Yes" && (
                                <NumberInput
                                    label="Period length"
                                    value={length}
                                    onChangeText={(value: NumericString) => setLength(value ? parseInt(value, 10) : 0)}
                                    min={0}
                                    max={Math.max(1, cycle.cycle)}
                                    right="days"
                                />
                            )}
                        </View>
                        <View style={Padding.horizontal_md}>
                            <Button
                                title="Continue"
                                variant="primary"
                                size="lg"
                                disabled={!predictable}
                                onPress={savePeriod}
                            />
                        </View>
                    </View>
                </Page>
            </Authorize>
        </>
    );
}
